import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Save, Send, FileText, Shield } from 'lucide-react'
import { GovernmentNav } from '../../components/navigation/GovernmentNav'
import { GovRfqApi, GovRfqData } from '../../modules/government/api/govRfqApi'
import { useAuthStore } from '../../store/authStore'
import { toast } from 'react-toastify'

export const CreateGovRfqPage: React.FC = () => {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const [loading, setLoading] = useState(false)
  const [formData, setFormData] = useState<Partial<GovRfqData>>({
    title: '',
    description: '',
    category: '',
    budget: 0,
    deadline: '',
    requirements: [],
    complianceRequirements: {
      cageCode: true,
      dunsNumber: true,
      gsaSchedule: false,
      certifications: [],
      securityClearance: 'none',
      pastPerformance: true
    },
    solicitationNumber: '',
    naicsCode: '',
    setAside: 'full-and-open',
    contractType: 'fixed-price',
    status: 'draft'
  })

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleComplianceChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      complianceRequirements: {
        ...prev.complianceRequirements!,
        [field]: value
      }
    }))
  }

  const handleAddRequirement = () => {
    setFormData(prev => ({
      ...prev,
      requirements: [...(prev.requirements || []), '']
    }))
  }

  const handleRequirementChange = (index: number, value: string) => {
    setFormData(prev => ({
      ...prev,
      requirements: prev.requirements?.map((req, i) => i === index ? value : req) || []
    }))
  }

  const handleRemoveRequirement = (index: number) => {
    setFormData(prev => ({
      ...prev,
      requirements: prev.requirements?.filter((_, i) => i !== index) || []
    }))
  }

  const handleSaveDraft = async () => {
    if (!user) return

    setLoading(true)
    try {
      // Only send the fields that the backend expects
      const rfqData = {
        title: formData.title || '',
        description: formData.description || '',
        budgetRange: `$${formData.budget || 0}`,
        location: 'Not specified', // Default location since form doesn't have this field
        deadline: formData.deadline || ''
      }

      await GovRfqApi.createRfq(rfqData)
      toast.success('RFQ saved as draft successfully')
      navigate('/government')
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to save RFQ draft')
    } finally {
      setLoading(false)
    }
  }

  const handlePublish = async () => {
    if (!user) return

    setLoading(true)
    try {
      // Only send the fields that the backend expects
      const rfqData = {
        title: formData.title || '',
        description: formData.description || '',
        budgetRange: `$${formData.budget || 0}`,
        location: 'Not specified', // Default location since form doesn't have this field
        deadline: formData.deadline || ''
      }

      await GovRfqApi.createRfq(rfqData)
      toast.success('RFQ published successfully')
      navigate('/government')
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to publish RFQ')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <GovernmentNav />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/government')}
            className="flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </button>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
            Create Government RFQ
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            Create a new Request for Quote for government contracting
          </p>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border dark:border-gray-700 p-6">
          <form className="space-y-8">
            {/* Basic Information */}
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center">
                <FileText className="w-5 h-5 mr-2" />
                Basic Information
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    RFQ Title *
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => handleInputChange('title', e.target.value)}
                    className="input"
                    placeholder="Enter RFQ title"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Solicitation Number *
                  </label>
                  <input
                    type="text"
                    value={formData.solicitationNumber}
                    onChange={(e) => handleInputChange('solicitationNumber', e.target.value)}
                    className="input"
                    placeholder="e.g., RFQ-2024-001"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Category *
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) => handleInputChange('category', e.target.value)}
                    className="input"
                    required
                  >
                    <option value="">Select Category</option>
                    <option value="IT Services">IT Services</option>
                    <option value="Construction">Construction</option>
                    <option value="Professional Services">Professional Services</option>
                    <option value="Equipment">Equipment</option>
                    <option value="Supplies">Supplies</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    NAICS Code *
                  </label>
                  <input
                    type="text"
                    value={formData.naicsCode}
                    onChange={(e) => handleInputChange('naicsCode', e.target.value)}
                    className="input"
                    placeholder="e.g., 541511"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Budget *
                  </label>
                  <input
                    type="number"
                    value={formData.budget}
                    onChange={(e) => handleInputChange('budget', parseFloat(e.target.value))}
                    className="input"
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Response Deadline *
                  </label>
                  <input
                    type="datetime-local"
                    value={formData.deadline}
                    onChange={(e) => handleInputChange('deadline', e.target.value)}
                    className="input"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Set-Aside *
                  </label>
                  <select
                    value={formData.setAside}
                    onChange={(e) => handleInputChange('setAside', e.target.value)}
                    className="input"
                    required
                  >
                    <option value="full-and-open">Full and Open</option>
                    <option value="small-business">Small Business</option>
                    <option value="8a">8(a) Business Development</option>
                    <option value="women-owned">Women-Owned Small Business</option>
                    <option value="veteran-owned">Veteran-Owned Small Business</option>
                    <option value="service-disabled">Service-Disabled Veteran-Owned</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Contract Type *
                  </label>
                  <select
                    value={formData.contractType}
                    onChange={(e) => handleInputChange('contractType', e.target.value)}
                    className="input"
                    required
                  >
                    <option value="fixed-price">Fixed Price</option>
                    <option value="cost-plus">Cost Plus</option>
                    <option value="time-and-materials">Time and Materials</option>
                    <option value="indefinite-delivery">Indefinite Delivery</option>
                  </select>
                </div>
              </div>
              <div className="mt-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Description *
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  className="textarea"
                  rows={4}
                  placeholder="Describe the requirements and scope of work"
                  required
                />
              </div>
            </div>

            {/* Requirements */}
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
                Requirements
              </h2>
              <div className="space-y-3">
                {formData.requirements?.map((requirement, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <input
                      type="text"
                      value={requirement}
                      onChange={(e) => handleRequirementChange(index, e.target.value)}
                      className="input flex-1"
                      placeholder="Enter requirement"
                    />
                    <button
                      type="button"
                      onClick={() => handleRemoveRequirement(index)}
                      className="p-2 text-red-600 hover:text-red-800"
                    >
                      Remove
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={handleAddRequirement}
                  className="btn btn-outline"
                >
                  Add Requirement
                </button>
              </div>
            </div>

            {/* Compliance Requirements */}
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center">
                <Shield className="w-5 h-5 mr-2" />
                Compliance Requirements
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.complianceRequirements?.cageCode}
                      onChange={(e) => handleComplianceChange('cageCode', e.target.checked)}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">CAGE Code Required</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.complianceRequirements?.dunsNumber}
                      onChange={(e) => handleComplianceChange('dunsNumber', e.target.checked)}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">DUNS Number Required</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.complianceRequirements?.gsaSchedule}
                      onChange={(e) => handleComplianceChange('gsaSchedule', e.target.checked)}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">GSA Schedule Required</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.complianceRequirements?.pastPerformance}
                      onChange={(e) => handleComplianceChange('pastPerformance', e.target.checked)}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">Past Performance Required</span>
                  </label>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Security Clearance
                  </label>
                  <select
                    value={formData.complianceRequirements?.securityClearance}
                    onChange={(e) => handleComplianceChange('securityClearance', e.target.value)}
                    className="input"
                  >
                    <option value="none">None Required</option>
                    <option value="public-trust">Public Trust</option>
                    <option value="secret">Secret</option>
                    <option value="top-secret">Top Secret</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={handleSaveDraft}
                disabled={loading}
                className="btn btn-outline flex items-center"
              >
                <Save className="w-4 h-4 mr-2" />
                Save Draft
              </button>
              <button
                type="button"
                onClick={handlePublish}
                disabled={loading}
                className="btn btn-primary flex items-center"
              >
                <Send className="w-4 h-4 mr-2" />
                Publish RFQ
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}